import { useCallback, useState } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { toast } from 'sonner';
import { isBadRequestError } from '@/core/api/badRequestError';
import { orderCommentActionService } from '@/entities/Order/api/orderCommentActionService';
import {
    orderCommentToApproveRequest,
    orderCommentToScheduleRequest,
} from '@/entities/Order/orderCommentActionMappers';
import type { OrderCommentActionFormValues } from '@/entities/Order/model/orderCommentActionSchema';
import { getErrorMessage } from '@/shared/lib/errors';

export type OrderCommentAction = 'approveExecutor' | 'startExecutorSearch';

/** Отправляет действие с исполнителем заказа и показывает его результат. */
export const useOrderCommentActionSubmit = (
    action: OrderCommentAction,
    orderId: string,
    form: UseFormReturn<OrderCommentActionFormValues>,
    successMessage: string,
    errorMessage: string,
) => {
    const [completed, setCompleted] = useState(false);

    /** Выполняет выбранное действие с заказом. */
    const onSubmit = useCallback(async (values: OrderCommentActionFormValues) => {
        form.clearErrors('root.server');

        try {
            if (action === 'approveExecutor') {
                await orderCommentActionService.approveExecutor(orderCommentToApproveRequest(values, orderId));
            } else {
                await orderCommentActionService.startExecutorSearch(orderCommentToScheduleRequest(values, orderId));
            }

            form.reset(values);
            setCompleted(true);
            toast.success(successMessage);
        } catch (error: unknown) {
            if (isBadRequestError(error)) return;

            form.setError('root.server', {
                type: 'server',
                message: getErrorMessage(error, errorMessage),
            });
        }
    }, [action, errorMessage, form, orderId, successMessage]);

    /** Сбрасывает результат предыдущего действия. */
    const resetCompleted = useCallback(() => setCompleted(false), []);

    return {
        onSubmit,
        completed,
        resetCompleted
    };
};
